import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { Card, Typography, Space, Button, Row, Col, Table, message, Spin, Alert, Select, Modal, DatePicker, Checkbox } from 'antd';
import { AppstoreOutlined, BarChartOutlined, LineChartOutlined, StockOutlined, RiseOutlined, DollarOutlined, LoginOutlined, CalendarOutlined } from '@ant-design/icons';
import Chart from 'react-apexcharts';
import { Container } from 'react-bootstrap';
import { Nav, Navbar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../user/auth/AuthContext';
import { useCompany } from '../../shared/context/CompanyContext';
import { API_CONFIG, apiClient } from '../../api/config';
import { logger } from '../../utils/logger';
import activityTracker from '../user/ActivityTracker';
import TechnicalChartsPage from '../technical/TechnicalChartsPage';
import LoginDialog from '../user/auth/LoginDialog';
import usePageTracking from '../../utils/usePageTracking';
import RegisterDialog from '../user/auth/RegisterDialog';
import PageviewLimitChecker from '../user/pageviewLimitChecker';
import { 
  basicInfoColumns,
  growthValuationColumns,
  financialRatiosColumns,
  pricePerformanceColumns,
  trendColumns,
  pageNames
} from '../../shared/components/TableColumns';
import TableControlBar, { useColumnVisibility } from '../../shared/components/ColumnVisibilityControl';

const { Title } = Typography;
const { Option } = Select;

function TopCompanies() {
  // GA4 页面追踪
  usePageTracking('市場動能列表');
  
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { updateCompanyInfo } = useCompany();
  const [loading, setLoading] = useState(false);
  const [companiesData, setCompaniesData] = useState([]);
  const [tableViewType, setTableViewType] = useState('basic');
  const [activeTopTab, setActiveTopTab] = useState('momentum'); // 'momentum' | 'rise' | 'drop'
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [showLimitChecker, setShowLimitChecker] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedPerfPeriod, setSelectedPerfPeriod] = useState('1D'); // '1D' | '5D' | '1M' | '3M' | '1Y'
  const [momentumData, setMomentumData] = useState([]);
  const [momentumLoading, setMomentumLoading] = useState(false);
  const [showUp50MA, setShowUp50MA] = useState(true);
  const [showUp100MA, setShowUp100MA] = useState(true);
  const [showUp200MA, setShowUp200MA] = useState(true);
  const [showBullMA, setShowBullMA] = useState(true);
  const tableContainerRef = useRef(null);


  useEffect(() => {
    activityTracker.trackPageView('top_momentum');
  }, []);

  // 检查访客状态
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      // 如果是访客用户，显示登录提示
      setShowLoginModal(true);
    }
  }, [authLoading, isAuthenticated]);

  // 检查浏览限制
  const checkViewLimit = async () => {
    if (!isAuthenticated || !user?.user_id) {
      logger.log('用户未登录或没有用户ID，跳过浏览限制检查');
      return;
    }

    try {
      logger.log('开始检查 topcompanies 浏览限制，用户ID:', user.user_id);
      logger.log('API 端点:', API_CONFIG.endpoints.TOPCOMPANIES.CHECK_VIEW_LIMIT);
      
      const response = await apiClient.get(API_CONFIG.endpoints.TOPCOMPANIES.CHECK_VIEW_LIMIT, {
        params: { userId: user.user_id }
      });

      logger.log('API 响应:', response.data);

      if (response.data?.success) {
        const limitData = response.data.data;
        setLimitInfo(limitData);
        
        if (!limitData.hasAccess) {
          // 如果超过限制，显示限制检查器
          setShowLimitChecker(true);
        }
        
        logger.log('浏览限制检查结果:', limitData);
      } else {
        logger.error('API 返回失败:', response.data);
      }
    } catch (error) {
      logger.error('检查浏览限制失败:', error);
      logger.error('错误详情:', error.response?.data);
    }
  };

  // 当用户已认证时检查浏览限制
  useEffect(() => {
    if (isAuthenticated && user?.user_id) {
      checkViewLimit();
    }
  }, [isAuthenticated, user?.user_id]);

  // 依分頁自動載入資料
  useEffect(() => {
    // 只有在用户已认证且有访问权限时才加载数据
    if (isAuthenticated && (!limitInfo || limitInfo.hasAccess)) {
    if (activeTopTab === 'momentum') {
      fetchMomentumData();
    } else if (activeTopTab === 'rise') {
        // 如果是1D期間且已選擇特定日期，不自動載入資料（由日期選擇處理）
        if (selectedPerfPeriod === '1D' && selectedDate && !selectedDate.isSame(dayjs(), 'day')) {
          // 不自動載入，讓日期選擇處理
          return;
        }
        // 使用新 top-perf 端點依選擇期間排序
        fetchTopPerf(selectedPerfPeriod);
      } else if (activeTopTab === 'drop') {
        fetchTopDrop();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTopTab, isAuthenticated, limitInfo, selectedPerfPeriod]);

  // 處理點擊公司代碼的事件
  const handleSymbolClick = async (symbol) => {
    activityTracker.trackBtnExecution('top_momentum', 'symbol_click', `股票代碼點擊_${symbol}`);
    logger.log('TopCompanies: handleSymbolClick 被調用，symbol =', symbol);
    
    if (!isAuthenticated) {
      // 获取当前搜索次数和时间戳
      const lastResetTime = localStorage.getItem('companySearchCountTime');
      const currentTime = new Date().getTime();
      
      // 如果没有时间戳或超过4天，重置计数
      if (!lastResetTime || (currentTime - parseInt(lastResetTime)) > (4 * 24 * 60 * 60 * 1000)) {
        localStorage.setItem('companySearchCount', '0');
        localStorage.setItem('companySearchCountTime', currentTime.toString());
        logger.log('重置搜索計數，因為超過4天');
      }

      // 获取更新后的计数
      const newCount = parseInt(localStorage.getItem('companySearchCount')) + 1;
      localStorage.setItem('companySearchCount', newCount.toString());
      localStorage.setItem('companySearchCountTime', currentTime.toString());

      // 如果超过3次，显示登录提示
      if (newCount > 3) {
        message.warning('您已達到未登錄用戶的搜索限制(3次)，請登錄以繼續使用完整功能。');
        return;
      }
    }

    try {
      logger.log('TopCompanies: 開始獲取公司信息，symbol =', symbol);
      
      // 調用公司搜索 API
      const response = await apiClient.get(API_CONFIG.endpoints.companySearch, {
        params: { term: symbol }
      });
      logger.log('TopCompanies: API 響應數據 =', response.data);
      
      if (response.data && response.data.length > 0) {
        const companyInfo = response.data[0];
        logger.log('TopCompanies: 獲取到公司信息 =', companyInfo);
        
        // 更新公司信息
        updateCompanyInfo(companyInfo);
        
        // 導航到資訊主頁
        navigate('/maininfo');
      } else {
        logger.error('TopCompanies: 未找到公司信息');
        navigate('/maininfo');
      }
    } catch (error) {
      logger.error('TopCompanies: 獲取公司信息失敗 =', error);
      navigate('/maininfo');
    }
  };

  // 處理右鍵選單事件
  const handleSymbolContextMenu = async (e, symbol) => {
    e.preventDefault();
    
    // 先清理可能存在的舊選單
    const existingMenu = document.querySelector('.symbol-context-menu');
    if (existingMenu) {
      try {
        existingMenu.remove();
      } catch (error) {
        // 忽略錯誤，選單可能已經不存在
      }
    }
    
    // 創建自定義右鍵選單
    const menu = document.createElement('div');
    menu.className = 'symbol-context-menu';
    menu.style.cssText = `
      position: fixed;
      top: ${e.clientY}px;
      left: ${e.clientX}px;
      background: white;
      border: 1px solid #d9d9d9;
      border-radius: 6px;
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
      z-index: 1000;
      min-width: 120px;
      font-size: 14px;
    `;

    const menuItems = [
      { text: '新分頁開啟', action: () => openSymbolInNewTab(symbol) },
      { text: '新視窗開啟', action: () => openSymbolInNewWindow(symbol) }
    ];

    menuItems.forEach(item => {
      const menuItem = document.createElement('div');
      menuItem.textContent = item.text;
      menuItem.style.cssText = `
        padding: 8px 12px;
        cursor: pointer;
        border-bottom: 1px solid #f0f0f0;
        transition: background-color 0.2s;
      `;
      menuItem.onmouseover = () => {
        menuItem.style.backgroundColor = '#f5f5f5';
      };
      menuItem.onmouseout = () => {
        menuItem.style.backgroundColor = 'white';
      };
      menuItem.onclick = () => {
        item.action();
        closeMenu();
      };
      menu.appendChild(menuItem);
    });

    // 移除最後一個項目的底部邊框
    const lastItem = menu.lastElementChild;
    if (lastItem) {
      lastItem.style.borderBottom = 'none';
    }

    document.body.appendChild(menu);

    // 點擊其他地方關閉選單
    let isMenuClosed = false;
    const closeMenu = () => {
      if (isMenuClosed) return; // 防止重複執行
      isMenuClosed = true;
      
      const menuToRemove = document.querySelector('.symbol-context-menu');
      if (menuToRemove) {
        try {
          menuToRemove.remove();
        } catch (error) {
          // 忽略錯誤，選單可能已經不存在
        }
      }
      
      // 移除事件監聽器
      document.removeEventListener('click', closeMenu);
      document.removeEventListener('contextmenu', closeMenu);
    };

    // 延遲添加事件監聽器，避免立即觸發
    setTimeout(() => {
      document.addEventListener('click', closeMenu);
      document.addEventListener('contextmenu', closeMenu);
    }, 100);
  };

  // 在新分頁中開啟 symbol
  const openSymbolInNewTab = async (symbol) => {
    try {
      // 調用公司搜索 API
      const response = await apiClient.get(API_CONFIG.endpoints.companySearch, {
        params: { term: symbol }
      });
      
      if (response.data && response.data.length > 0) {
        const companyInfo = response.data[0];
        
        // 將公司信息存儲到 localStorage，供新分頁使用
        localStorage.setItem('tempCompanyInfo', JSON.stringify(companyInfo));
        
        // 在新分頁中開啟資訊主頁，並帶上 symbol 參數
        const newTab = window.open(`/maininfo?symbol=${encodeURIComponent(symbol)}`, '_blank');
        
        // 如果新分頁被阻擋，顯示提示
        if (!newTab) {
          message.warning('請允許彈出視窗以在新分頁中開啟');
        }
      } else {
        message.error('未找到公司信息');
      }
    } catch (error) {
      logger.error('在新分頁開啟 symbol 失敗:', error);
      message.error('開啟失敗，請稍後再試');
    }
  };

  // 在新視窗中開啟 symbol
  const openSymbolInNewWindow = async (symbol) => {
    try {
      // 調用公司搜索 API
      const response = await apiClient.get(API_CONFIG.endpoints.companySearch, {
        params: { term: symbol }
      });
      
      if (response.data && response.data.length > 0) {
        const companyInfo = response.data[0];
        
        // 將公司信息存儲到 localStorage，供新視窗使用
        localStorage.setItem('tempCompanyInfo', JSON.stringify(companyInfo));
        
        // 在新視窗中開啟資訊主頁，並帶上 symbol 參數
        const newWindow = window.open(`/maininfo?symbol=${encodeURIComponent(symbol)}`, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
        
        // 如果新視窗被阻擋，顯示提示
        if (!newWindow) {
          message.warning('請允許彈出視窗以在新視窗中開啟');
        }
      } else {
        message.error('未找到公司信息');
      }
    } catch (error) {
      logger.error('在新視窗開啟 symbol 失敗:', error);
      message.error('開啟失敗，請稍後再試');
    }
  };


  // 獲取市場動能數據
  const fetchMomentumData = async () => {
    try {
      setMomentumLoading(true);
      activityTracker.trackBtnExecution('top_momentum', 'fetch_momentum', '獲取市場動能數據');

      const response = await apiClient.get(API_CONFIG.endpoints.TOPCOMPANIES.MARKET_MOMENTUM);
      
      if (response.data && response.data.length > 0) {
        setMomentumData(response.data);
        //message.success(`成功獲取 ${response.data.length} 筆市場動能數據`);
      } else {
        setMomentumData([]);
        message.warning('未找到市場動能數據');
      }
    } catch (error) {
      logger.error('獲取市場動能數據時出錯:', error);
      message.error('獲取數據失敗，請稍後再試');
    } finally {
      setMomentumLoading(false);
    }
  };

  // 獲取市場動能 ADL 數據

  // 獲取漲幅最大的50檔股票
  const fetchTopRise = async () => {
    try {
      setLoading(true);
      activityTracker.trackBtnExecution('top_momentum', 'fetch_rise', '獲取漲幅最大50檔股票');

      const response = await apiClient.get(API_CONFIG.endpoints.TOPCOMPANIES.TOP_RISE);
      
      if (Array.isArray(response.data) && response.data.length > 0) {
        setCompaniesData(response.data);
        message.success(`成功獲取 ${response.data.length} 家漲幅最大的公司`);
      } else {
        setCompaniesData([]);
        message.warning('未找到漲幅榜公司數據');
      }
    } catch (error) {
      logger.error('獲取漲幅最大50檔股票時出錯:', error);
      message.error('獲取數據失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  // 依表現期間獲取前50檔股票（新 top-perf）
  const fetchTopPerf = async (period) => {
    try {
      setLoading(true);
      activityTracker.trackBtnExecution('top_momentum', 'fetch_top_perf', `獲取表現(${period})前50檔股票`);

      const response = await apiClient.get(API_CONFIG.endpoints.TOPCOMPANIES.TOP_PERF, {
        params: { period }
      });

      if (Array.isArray(response.data) && response.data.length > 0) {
        setCompaniesData(response.data);
        message.success(`成功獲取 ${response.data.length} 家表現前列的公司`);
      } else {
        setCompaniesData([]);
        message.warning('未找到符合條件的公司數據');
      }
    } catch (error) {
      logger.error('獲取 top-perf 數據時出錯:', error);
      message.error('獲取數據失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  // 處理日期選擇（僅在當日 1D 時顯示/生效）
  const handleDateChange = (date, dateString) => {
    setSelectedDate(date);
    if (dateString && activeTopTab === 'rise' && selectedPerfPeriod === '1D') {
      // 立即載入指定日期的資料，避免顯示當日資料
      fetchTopRiseByDate(dateString);
    }
  };

  // 獲取跌幅最大的50檔股票
  const fetchTopDrop = async () => {
    try {
      setLoading(true);
      activityTracker.trackBtnExecution('top_momentum', 'fetch_drop', '獲取跌幅最大50檔股票');

      const response = await apiClient.get(API_CONFIG.endpoints.TOPCOMPANIES.TOP_DROP);
      
      if (Array.isArray(response.data) && response.data.length > 0) {
        setCompaniesData(response.data);
        message.success(`成功獲取 ${response.data.length} 家跌幅最大的公司`);
      } else {
        setCompaniesData([]);
        message.warning('未找到跌幅榜公司數據');
      }
    } catch (error) {
      logger.error('獲取跌幅最大50檔股票時出錯:', error);
      message.error('獲取數據失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  // 獲取指定日期的漲幅最大50檔股票
  const fetchTopRiseByDate = async (date) => {
    try {
      setLoading(true);
      activityTracker.trackBtnExecution('top_momentum', 'fetch_rise_date', `獲取${date}漲幅最大50檔股票`);

      const response = await apiClient.get(API_CONFIG.endpoints.TOPCOMPANIES.TOP_RISE_DATE, {
        params: { date }
      });
      
      if (Array.isArray(response.data) && response.data.length > 0) {
        setCompaniesData(response.data);
        message.success(`成功獲取 ${date} 的 ${response.data.length} 家漲幅最大的公司`);
      } else {
        setCompaniesData([]);
        message.warning(`未找到 ${date} 的漲幅榜公司數據`);
      }
    } catch (error) {
      logger.error('獲取指定日期漲幅最大50檔股票時出錯:', error);
      message.error('獲取數據失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };


  const handleTableViewChange = (viewType) => {
    setTableViewType(viewType);
  };

  // 處理「漲最多」期間選擇
  const handlePerfPeriodChange = (value) => {
    setSelectedPerfPeriod(value);
    if (activeTopTab !== 'rise') {
      setActiveTopTab('rise');
      return;
    }
    
    // 如果是1D期間且已選擇特定日期，載入指定日期的資料
    if (value === '1D' && selectedDate && !selectedDate.isSame(dayjs(), 'day')) {
      fetchTopRiseByDate(selectedDate.format('YYYY-MM-DD'));
    } else {
      // 其他情況由 useEffect 處理
      fetchTopPerf(value);
    }
  };

  // 處理頂部標籤切換
  const handleTopTabChange = (tab) => {
    setActiveTopTab(tab);
    // 如果是漲最多或跌最多，自動切換到價格表現表格
    if (tab === 'rise' || tab === 'drop') {
      setTableViewType('price');
    }
  };

  // 動能圖表配置
  const getMomentumChartOptions = () => {
    // 後端已經確保返回 YYYY-MM-DD 格式，直接使用
    const dates = momentumData.map(item => {
      if (!item.date) return '';
      // 確保只取日期部分，去掉可能的時間部分
      return item.date.split(' ')[0].split('T')[0];
    }).filter(date => date !== ''); // 過濾掉無效日期

    const up50Data = momentumData.map(item => parseFloat(item.up50MA) || 0);
    const up100Data = momentumData.map(item => parseFloat(item.up100MA) || 0);
    const up200Data = momentumData.map(item => parseFloat(item.up200MA) || 0);
    const bullMAData = momentumData.map(item => parseFloat(item.BullMA) || 0);

    return {
      chart: {
        type: 'line',
        height: 400,
        toolbar: {
          show: true
        }
      },
      xaxis: {
        categories: dates,
        title: {
          text: '日期'
        },
        type: 'category',
        labels: {
          formatter: function (val) {
            // 確保只顯示日期格式 YYYY-MM-DD
            let dateStr = val;
            if (typeof val === 'string' && val.includes('T')) {
              dateStr = val.split('T')[0];
            }
            
            // 解析當前日期
            const currentDate = new Date(dateStr);
            if (isNaN(currentDate.getTime())) {
              return val;
            }
            
            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth(); // 0-11
            
            // 在 dates 數組中找到當前日期的索引
            const currentIndex = dates.findIndex(d => {
              let dStr = d;
              if (typeof d === 'string' && d.includes('T')) {
                dStr = d.split('T')[0];
              }
              return dStr === dateStr;
            });
            
            // 檢查是否為月份轉換
            if (currentIndex > 0) {
              // 獲取前一個日期
              const prevDateStr = dates[currentIndex - 1];
              
              if (!prevDateStr) {
                return '';
              }
              
              let prevDateStrClean = prevDateStr;
              if (typeof prevDateStr === 'string' && prevDateStr.includes('T')) {
                prevDateStrClean = prevDateStr.split('T')[0];
              }
              
              const prevDate = new Date(prevDateStrClean);
              if (!isNaN(prevDate.getTime())) {
                const prevYear = prevDate.getFullYear();
                const prevMonth = prevDate.getMonth();
                
                // 如果年份或月份不同，則為月份轉換
                if (currentYear !== prevYear || currentMonth !== prevMonth) {
                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  
                  // 如果是一月份，顯示年份
                  if (currentMonth === 0) {
                    return `${currentYear}-${monthNames[currentMonth]}`;
                  } else {
                    return monthNames[currentMonth];
                  }
                }
              }
            } else if (currentIndex === 0) {
              // 第一個數據點，顯示月份
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              
              if (currentMonth === 0) {
                return `${currentYear}-${monthNames[currentMonth]}`;
              } else {
                return monthNames[currentMonth];
              }
            }
            
            // 不是月份轉換，不顯示標籤
            return '';
          },
          rotate: -45,
          style: {
            fontSize: '12px'
          }
        }
      },
      yaxis: {
        title: {
          text: '百分比 (%)'
        },
        labels: {
          formatter: function (val) {
            return val.toFixed(1) + '%';
          }
        }
      },
      series: [
        ...(showUp50MA ? [{
          name: 'Up 50MA',
          data: up50Data,
          color: '#ff6b6b'
        }] : []),
        ...(showUp100MA ? [{
          name: 'Up 100MA',
          data: up100Data,
          color: '#4ecdc4'
        }] : []),
        ...(showUp200MA ? [{
          name: 'Up 200MA',
          data: up200Data,
          color: '#45b7d1'
        }] : []),
        ...(showBullMA ? [{
          name: 'Bull MA',
          data: bullMAData,
          color: '#f39c12'
        }] : [])
      ],
      stroke: {
        width: 2
      },
      markers: {
        size: 0 // 移除小圓點
      },
      legend: {
        position: 'top'
      },
      grid: {
        borderColor: '#f1f1f1'
      },
      tooltip: {
        x: {
          formatter: function (val, { dataPointIndex }) {
            // 顯示完整的年月日日期
            const dateStr = dates[dataPointIndex];
            if (!dateStr) return val;
            
            let displayDate = dateStr;
            if (typeof dateStr === 'string' && dateStr.includes('T')) {
              displayDate = dateStr.split('T')[0];
            }
            
            // 解析日期並格式化為更易讀的格式
            const date = new Date(displayDate);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = date.getMonth() + 1; // 1-12
              const day = date.getDate();
              return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
            }
            
            return displayDate;
          }
        },
        y: {
          formatter: function (val) {
            return val.toFixed(2) + '%';
          }
        }
      }
    };
  };


  // 处理登录模态框
  const handleLoginModalOk = () => {
    setShowLoginModal(false);
    setShowLoginDialog(true);
  };

  const handleLoginModalCancel = () => {
    setShowLoginModal(false);
    navigate('/');
  };

  // 表格列定義 - 為公司代碼欄位添加點擊事件處理
  const getColumns = () => {
    const addClickHandlers = (columns) => {
      return columns.map(col => {
        if (col.dataIndex === 'company_symbol') {
          return {
            ...col,
            render: (text, record) => {
              const symbol = record?.company_symbol || record?.symbol || text;
              return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  overflow: 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '4px',
                  flexShrink: 0
                }}>
                  {record.image ? (
                    <img 
                      src={record.image} 
                      alt={symbol} 
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'contain' 
                      }}
                      onError={(e) => {
                        try {
                          const imgEl = e.currentTarget;
                          if (imgEl && imgEl.style) {
                            imgEl.style.display = 'none';
                          }
                          const parent = imgEl ? imgEl.parentElement : null;
                          if (!parent) return;
                          // 移除可能已存在的fallback
                          const existing = parent.querySelector('.img-fallback-initial');
                          if (existing) existing.remove();
                          const fallback = document.createElement('span');
                          fallback.className = 'img-fallback-initial';
                          fallback.textContent = (symbol && typeof symbol === 'string') ? symbol[0] : 'N/A';
                          fallback.style.fontSize = '12px';
                          fallback.style.color = '#999';
                          parent.appendChild(fallback);
                        } catch (_) {
                          // 忽略任何錯誤，避免影響渲染
                        }
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      {symbol ? symbol[0] : 'N/A'}
                    </span>
                  )}
                </div>
                <a 
                  onClick={() => handleSymbolClick(symbol)}
                  onContextMenu={(e) => handleSymbolContextMenu(e, symbol)}
                  style={{ 
                    color: '#1890ff',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    fontSize: '13px'
                  }}
                  className="company-symbol-link"
                >
                  {symbol}
                </a>
              </div>
            );
            }
          };
        }
        
        // 為漲跌幅欄位添加特殊樣式（在跌最多分頁時）
        if (col.dataIndex === 'changesPercentage' && activeTopTab === 'drop') {
          return {
            ...col,
            render: (value) => {
              if (!value) return '-';
              const numValue = parseFloat(value);
              const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
              return (
                <span style={{ 
                  color, 
                  backgroundColor: '#fffbe6', // 淺黃色底色
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontWeight: 'bold'
                }}>
                  {numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%
                </span>
              );
            }
          };
        }

        // 為各期間漲幅欄位添加特殊樣式（在漲最多分頁時，根據選擇的期間）
        if (activeTopTab === 'rise') {
          const periodColumnMap = {
            '1D': 'changesPercentage',
            '5D': 'perf_5D',
            '1M': 'perf_1M',
            '3M': 'perf_3M',
            '1Y': 'perf_1Y'
          };
          
          const targetColumn = periodColumnMap[selectedPerfPeriod];
          if (col.dataIndex === targetColumn) {
            return {
              ...col,
              render: (value) => {
                if (!value) return '-';
                const numValue = parseFloat(value);
                const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
                return (
                  <span style={{ 
                    color, 
                    backgroundColor: '#fffbe6', // 淺黃色底色
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontWeight: 'bold'
                  }}>
                    {numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%
                  </span>
                );
              }
            };
          }
        }
        
        return col;
      });
    };

    switch (tableViewType) {
      case 'growth':
        return addClickHandlers(growthValuationColumns);
      case 'financial':
        return addClickHandlers(financialRatiosColumns);
      case 'price':
        return addClickHandlers(pricePerformanceColumns);
      case 'trend':
        return addClickHandlers(trendColumns);
      case 'charts':
        return []; // 圖表視圖不需要表格列
      case 'basic':
      default:
        return addClickHandlers(basicInfoColumns);
    }
  };

  // 使用列可見性控制Hook
  const { visibleColumns, setVisibleColumns, filteredColumns: visibilityFilteredColumns } = useColumnVisibility(getColumns());

  // 組件卸載時清理右鍵選單
  useEffect(() => {
    return () => {
      // 清理所有可能存在的右鍵選單
      const existingMenus = document.querySelectorAll('.symbol-context-menu');
      existingMenus.forEach(menu => {
        try {
          menu.remove();
        } catch (error) {
          // 忽略錯誤
        }
      });
    };
  }, []);

  return (
    <Container fluid className="mt-4">
      {/* 登录提示模态框 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LoginOutlined style={{ color: '#57A300' }} />
            <span>需要登入</span>
          </div>
        }
        open={showLoginModal}
        onOk={handleLoginModalOk}
        onCancel={handleLoginModalCancel}
        okText="前往登入"
        cancelText="返回首頁"
        centered
        width={400}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Alert
            message="請登入後再觀看"
            description="此功能需要登入才能使用，請先登入您的帳戶以查看完整內容。"
            type="warning"
            showIcon
            style={{ marginBottom: '20px' }}
          />
          <p style={{ color: '#666', fontSize: '14px' }}>
            登入後您將可以：
          </p>
          <ul style={{ textAlign: 'left', color: '#666', fontSize: '14px', margin: '10px 0' }}>
            <li>查看完整的公司數據</li>
            <li>使用所有分析功能</li>
            <li>保存您的關注列表</li>
          </ul>
        </div>
      </Modal>

      {/* 如果用户未认证，不显示主要内容 */}
      {!isAuthenticated ? (
        <Card style={{ marginBottom: '20px' }}>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px', fontSize: '16px', color: '#666' }}>
              正在檢查登入狀態...
            </div>
          </div>
        </Card>
      ) : limitInfo && !limitInfo.hasAccess ? (
        <Card style={{ marginBottom: '20px' }}>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Alert
              message="已达到浏览限制"
              description={limitInfo.message}
              type="warning"
              showIcon
              style={{ marginBottom: '20px' }}
            />
            <Button type="primary" onClick={() => setShowLimitChecker(true)}>
              查看详情
            </Button>
          </div>
        </Card>
      ) : (
        <>
          {/* 頁面標題 */}
          <Card style={{ marginBottom: '5px' }}>
            <div style={{ textAlign: 'center', padding: '5px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>

                <Nav className="top-tabs-nav" style={{ border: '1px solid #57A300', borderRadius: 5, display: 'flex', alignItems: 'center', margin: 0 }}>
                  <Nav.Link onClick={() => handleTopTabChange('momentum')} className={`top-tabs-link ${activeTopTab === 'momentum' ? 'active' : ''}`}>
                    市場動能
                  </Nav.Link>
                  <Nav.Link onClick={() => handleTopTabChange('rise')} className={`top-tabs-link ${activeTopTab === 'rise' ? 'active' : ''}`}>
                    漲最多
                  </Nav.Link>
                  <Nav.Link onClick={() => handleTopTabChange('drop')} className={`top-tabs-link ${activeTopTab === 'drop' ? 'active' : ''}`}>
                    跌最多
                  </Nav.Link>
                </Nav>
               

              {activeTopTab === 'rise' && (
                <>
                  <Select
                    value={selectedPerfPeriod}
                    onChange={handlePerfPeriodChange}
                    placeholder="選擇期間"
                    style={{ width: 200 }}
                    size="middle"
                  >
                    <Option value="1D">當日</Option>
                    <Option value="5D">過去一週</Option>
                    <Option value="1M">過去一個月</Option>
                    <Option value="3M">過去一季</Option>
                    <Option value="1Y">過去一年</Option>
                  </Select>
                  {selectedPerfPeriod === '1D' && (
                    <DatePicker
                      value={selectedDate}
                      onChange={handleDateChange}
                      placeholder="選擇日期"
                      style={{ width: 200, marginLeft: 8 }}
                      size="middle"
                      format="YYYY-MM-DD"
                      allowClear={false}
                      suffixIcon={<CalendarOutlined />}
                      disabledDate={(current) => {
                        if (!current) return false;
                        
                        const today = dayjs();
                        const twoMonthsAgo = today.subtract(2, 'month');
                        
                        // 禁用當天以後的日期
                        if (current.isAfter(today, 'day')) {
                          return true;
                        }
                        
                        // 禁用過去兩個月以上的日期
                        if (current.isBefore(twoMonthsAgo, 'day')) {
                          return true;
                        }
                        
                        return false;
                      }}
                    />
                  )}
                </>
              )}
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: '5px 0' }}>
               <text style={{ fontSize: '14px', color: '#666', marginLeft: 10 }}> 以下的統計數據僅包含市值超過1億美元的上市公司</text>
            </div>
          </Card>

          {/* 載入狀態顯示 */}
          {(loading || momentumLoading ) && (
            <Card style={{ marginBottom: '20px' }}>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px', fontSize: '16px', color: '#666' }}>
                  正在載入中...
                </div>
              </div>
            </Card>
          )}

          {/* 市場動能圖表 */}
          {activeTopTab === 'momentum' && momentumData.length > 0 && (
            <Card style={{ marginTop: '20px' }}>
              <div style={{ marginBottom: '20px' }}>
                <Space>
                  <Checkbox 
                    checked={showUp50MA} 
                    onChange={(e) => setShowUp50MA(e.target.checked)}
                  >
                    Up50MA (50MA以上的股票比例)
                  </Checkbox>
                  <Checkbox 
                    checked={showUp100MA} 
                    onChange={(e) => setShowUp100MA(e.target.checked)}
                  >
                    Up100MA (100MA以上的股票比例)
                  </Checkbox>
                  <Checkbox 
                    checked={showUp200MA} 
                    onChange={(e) => setShowUp200MA(e.target.checked)}
                  >
                    Up200MA (200MA以上的股票比例)
                  </Checkbox>
                  <Checkbox 
                    checked={showBullMA} 
                    onChange={(e) => setShowBullMA(e.target.checked)}
                  >
                    BullMA (多頭排列股票比例)
                  </Checkbox>
                </Space>
              </div>
              <Chart
                options={getMomentumChartOptions()}
                series={getMomentumChartOptions().series}
                type="line"
                height={400}
              />
              
              {/* 動能指標說明 */}
              <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                <Title level={5} style={{ margin: '0 0 15px 0', color: '#57A300' }}>
                  📊 市場動能指標說明
                </Title>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <div style={{ padding: '10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #ff6b6b' }}>
                      <div style={{ fontWeight: 'bold', color: '#ff6b6b', marginBottom: '5px' }}>Up 50MA (紅色線)</div>
                      <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
                        <strong>意義：</strong>50日移動平均線上方股票比例<br/>
                        <strong>趨勢判斷：</strong><br/>
                        • 上升趨勢：市場短期動能強勁<br/>
                        • 下降趨勢：短期動能減弱<br/>
                        • 高點(&gt;75%)：可能過熱，需謹慎
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} md={8}>
                    <div style={{ padding: '10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #4ecdc4' }}>
                      <div style={{ fontWeight: 'bold', color: '#4ecdc4', marginBottom: '5px' }}>Up 100MA (青色線)</div>
                      <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
                        <strong>意義：</strong>100日移動平均線上方股票比例<br/>
                        <strong>趨勢判斷：</strong><br/>
                        • 上升趨勢：市場中期動能良好<br/>
                        • 下降趨勢：中期動能轉弱<br/>
                        • 高點(&gt;70%)：中期趨勢強勁
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} md={8}>
                    <div style={{ padding: '10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #45b7d1' }}>
                      <div style={{ fontWeight: 'bold', color: '#45b7d1', marginBottom: '5px' }}>Up 200MA (藍色線)</div>
                      <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
                        <strong>意義：</strong>200日移動平均線上方股票比例<br/>
                        <strong>趨勢判斷：</strong><br/>
                        • 上升趨勢：市場長期動能健康<br/>
                        • 下降趨勢：長期趨勢轉弱<br/>
                        • 高點(&gt;65%)：長期牛市特徵
                      </div>
                    </div>
                  </Col>
                </Row>
                
                {/* Bull MA 說明 - 橫跨三個說明下方 */}
                <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                  <Col xs={24}>
                    <div style={{ padding: '10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #f39c12' }}>
                      <div style={{ fontWeight: 'bold', color: '#f39c12', marginBottom: '5px' }}>Bull MA (橙色線)</div>
                      <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
                        <strong>意義：</strong>呈現多頭排列(50MA &gt; 100MA &gt; 200MA)的股票家數佔市場比例<br/>
                        <strong>趨勢判斷：</strong><br/>
                        • 上升趨勢：越來越多股票呈現多頭排列，市場結構健康   • 下降趨勢：多頭排列股票減少，市場結構轉弱<br/>
      
                        • 關注訊號：BullMA 由上升轉為下降 或 由下降轉上升 則須謹審慎觀察
                      </div>
                    </div>
                  </Col>
                </Row>
                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e8f5e8', borderRadius: '6px', border: '1px solid #57A300' }}>
                  <div style={{ fontWeight: 'bold', color: '#57A300', marginBottom: '8px' }}>💡 綜合判斷要點：</div>
                  <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                    • <strong>四線齊升：</strong>短中長期動能一致向上且多頭排列增加，市場強勢<br/>
                    • <strong>四線齊降：</strong>各時間框架動能轉弱且多頭排列減少，需謹慎<br/>
                    • <strong>BullMA領先：</strong>多頭排列比例高但動能線較低，市場結構優於動能<br/>
                    • <strong>動能線領先：</strong>動能線高但多頭排列比例低，短期動能強但結構待改善<br/>
                    • <strong>背離訊號：</strong>指數持續創高但四線比例卻下降，須謹慎觀察<br/>
                    • <strong>危險訊號：</strong>指數高點震盪但50MA急劇下降，需注意反轉風險<br/>
                  
                  </div>
                </div>
              </div>
            </Card>
          )}


          {/* 數據顯示區域 */}
          {companiesData.length > 0 && activeTopTab !== 'momentum' && (
        <Card style={{ marginTop: '20px' }}>
          {/* 表格视图导航栏 */}
            <Title level={5} style={{ margin: 0, color: '#57A300' }}>
                <DollarOutlined style={{ marginRight: '10px' }} />
                {activeTopTab === 'momentum' 
                  ? '市場動能趨勢圖'
                  : activeTopTab === 'rise' 
                    ? (selectedPerfPeriod === '1D' 
                        ? `${selectedDate ? selectedDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')} 漲幅前50大公司`
                        : (() => {
                            const labelMap = { '1D': '當日', '5D': '過去一週', '1M': '過去一個月', '3M': '過去一季', '1Y': '過去一年' };
                            return `${labelMap[selectedPerfPeriod] || ''} 漲幅前50大公司`;
                          })()
                      )
                    : '跌幅前50大公司'
                }
            </Title>
          <Navbar className="table-view-navbar">
            <Nav className="me-auto nav-center">
              <Nav.Link 
                onClick={() => handleTableViewChange('basic')}
                className={tableViewType === 'basic' ? 'active' : ''}
              >
                <AppstoreOutlined style={{ marginRight: '6px' }} />
                {pageNames.basic}
              </Nav.Link>
              <Nav.Link
                onClick={() => handleTableViewChange('growth')}
                className={tableViewType === 'growth' ? 'active' : ''}
              >
                <BarChartOutlined style={{ marginRight: '6px' }} />
                {pageNames.growth}
              </Nav.Link>
              <Nav.Link 
                onClick={() => handleTableViewChange('financial')}
                className={tableViewType === 'financial' ? 'active' : ''}
              >
                <BarChartOutlined style={{ marginRight: '6px' }} />
                {pageNames.financial}
              </Nav.Link>
              <Nav.Link 
                onClick={() => handleTableViewChange('price')}
                className={tableViewType === 'price' ? 'active' : ''}
              >
                <RiseOutlined style={{ marginRight: '6px' }} />
                {pageNames.price}
              </Nav.Link>
              <Nav.Link 
                onClick={() => handleTableViewChange('trend')}
                className={tableViewType === 'trend' ? 'active' : ''}
              >
                <LineChartOutlined style={{ marginRight: '6px' }} />
                {pageNames.trend}
              </Nav.Link>
              <Nav.Link 
                onClick={() => handleTableViewChange('charts')}
                className={tableViewType === 'charts' ? 'active' : ''}
              >
                <StockOutlined style={{ marginRight: '6px' }} />
                {pageNames.technical}
              </Nav.Link>
            </Nav>
          </Navbar>

          {/* 技術分析圖表視圖 */}
          {tableViewType === 'charts' && (
            <TechnicalChartsPage screeningResults={companiesData} />
          )}
          
          {/* 表格視圖 */}
          {tableViewType !== 'charts' && (
            <div ref={tableContainerRef}>
              <TableControlBar
                columns={getColumns()}
                visibleColumns={visibleColumns}
                onChange={setVisibleColumns}
                tableContainerRef={tableContainerRef}
              />
              <Table
              columns={visibilityFilteredColumns}
              dataSource={companiesData}
              rowKey={(record) => record.company_symbol || record.symbol || `${record.sector || 'row'}-${record.company_name || Math.random()}`}
              loading={loading}
              size="small"
              bordered
              pagination={{
                defaultPageSize: 100,
                showSizeChanger: true,
                pageSizeOptions: ['20', '50', '100', '200'],
              }}
              scroll={{ x: 'max-content' }}
              style={{ overflowX: 'auto' }}
              tableLayout="fixed"
              className="stock-screener-table"
              rowClassName={(record, index) => index % 2 === 0 ? 'even-row' : 'odd-row'}
              components={{
                header: {
                  cell: props => {
                    if (props.className && props.className.includes('icon-column')) {
                      return <th {...props} style={{ ...props.style, width: 30, maxWidth: 30, minWidth: 30 }} />;
                    }
                    return <th {...props} />;
                  }
                },
                body: {
                  cell: props => {
                    if (props.className && props.className.includes('icon-column')) {
                      return <td {...props} style={{ ...props.style, width: 30, maxWidth: 30, minWidth: 30 }} />;
                    }
                    return <td {...props} />;
                  }
                }
              }}
            />
            </div>
          )}
        </Card>
      )}

      <style jsx>{`
        /* Top tabs styles */
        .top-tabs-nav {
          border: 1px solid #57A300;
          border-radius: 5px;
        }
        .top-tabs-link {
          padding: 6px 16px;
          border-right: 1px solid #57A300; /* 分隔線 */
        }
        .top-tabs-link:last-child {
          border-right: none;
        }
        .top-tabs-link.active {
          background: #ff7a00; /* 橘色 */
          color: #fff !important;
        }

        /* 表格视图导航栏样式 */
        .table-view-navbar {
          background: none;
          border: none;
          box-shadow: none;
          margin: 0 0 15px 0;
          padding: 0;
        }
        .table-view-navbar .nav {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          align-items: center;
          gap: 0;
          overflow: hidden;
          border-radius: 24px;
          border: 2px solid #57A300;
          background: #fff;
          padding: 0;
        }
        .table-view-navbar .nav-center {
          justify-content: center;
          width: 100%;
        }
        .table-view-navbar .nav-link {
          border: 1px solid #57A300;
          background: #fff;
          color: #57A300;
          font-size: 14px;
          padding: 6px 20px;
          border-radius: 0;
          transition: background 0.2s, color 0.2s;
          box-shadow: none;
          position: relative;
          font-weight: 500;
          margin-left: -1px;
          cursor: pointer;
        }
        .table-view-navbar .nav-link:first-child {
          border-top-left-radius: 24px;
          border-bottom-left-radius: 24px;
          margin-left: 0;
        }
        .table-view-navbar .nav-link:last-child {
          border-top-right-radius: 24px;
          border-bottom-right-radius: 24px;
        }
        .table-view-navbar .nav-link.active {
          background: #57A300;
          color: #fff;
          z-index: 1;
        }
        
        @media screen and (max-width: 480px) {
          .table-view-navbar .nav {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
            white-space: nowrap !important;
            gap: 5px;
            justify-content: flex-start !important;
          }
          .table-view-navbar .nav-link {
            font-size: 12px !important;
            padding: 4px 12px !important;
          }
        }
      `}</style>

      {/* 添加自定义图标列样式 */}
      <style global jsx>{`
        .icon-column {
          width: 30px !important;
          min-width: 30px !important;
          max-width: 30px !important;
          padding: 0 !important;
          overflow: hidden !important;
        }
        .ant-table-cell.icon-column {
          width: 30px !important;
          min-width: 30px !important;
          max-width: 30px !important;
        }
        .ant-table-thead > tr > th.icon-column {
          width: 30px !important;
          min-width: 30px !important;
          max-width: 30px !important;
        }
        .ant-table-thead > tr > th.icon-column::before {
          display: none !important;
        }
        
        /* 公司代码链接样式 */
        .company-symbol-link:hover {
          color: #40a9ff !important;
          text-decoration: underline !important;
        }
        
        /* 强制表格遵守列宽设置 */
        .stock-screener-table .ant-table table {
          table-layout: fixed !important;
          width: 100% !important;
        }
        
        .stock-screener-table .ant-table-cell {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        /* 表头允许换行 */
        .stock-screener-table .ant-table-thead > tr > th {
          white-space: normal !important;
          word-wrap: break-word !important;
          word-break: break-word !important;
          line-height: 1.2 !important;
          vertical-align: middle !important;
        }
        
        .stock-screener-table .ant-table-thead > tr > th,
        .stock-screener-table .ant-table-tbody > tr > td {
          box-sizing: border-box !important;
          padding: 4px 8px !important;
        }
        
        /* 设置表头样式为绿色背景白色文字 */
        .stock-screener-table .ant-table-thead > tr > th {
          background-color: #57A300 !important;
          color: white !important;
          font-weight: 500 !important;
        }
        
        /* 去除表头分割线 */
        .stock-screener-table .ant-table-thead > tr > th::before {
          display: none !important;
        }
        
        /* 设置行样式 */
        .stock-screener-table .ant-table-tbody > tr.even-row > td {
          background-color: #f8f8f8;
        }
        
        .stock-screener-table .ant-table-tbody > tr.odd-row > td {
          background-color: #ffffff;
        }
        
        /* 鼠标悬停效果 */
        .stock-screener-table .ant-table-tbody > tr:hover > td {
          background-color: #e6f7e1 !important; /* 淡绿色 */
        }
        
        /* 确保图片容器不会扩张 */
        .stock-screener-table img {
          max-width: 100% !important;
          max-height: 100% !important;
        }
      `}</style>
        </>
      )}

      {/* 登录对话框 */}
      <LoginDialog 
        open={showLoginDialog} 
        onClose={() => setShowLoginDialog(false)} 
        openRegister={() => {
          setShowLoginDialog(false); 
          setShowRegisterDialog(true);
        }}
        isLimitReached={false}
      />

      {/* 注册对话框 */}
      <RegisterDialog 
        open={showRegisterDialog} 
        onClose={() => setShowRegisterDialog(false)}
        openLogin={() => {
          setShowRegisterDialog(false); 
          setShowLoginDialog(true);
        }}
      />

      {/* 浏览限制检查器 */}
      <PageviewLimitChecker 
        open={showLimitChecker} 
        onClose={() => setShowLimitChecker(false)}
        targetPage="top_momentum"
      />
    </Container>
  );
}

export default TopCompanies;
